import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { ArrowDownTrayIcon, MagnifyingGlassIcon, XMarkIcon, ArrowDownIcon, FolderIcon, ArrowUpIcon } from '@heroicons/react/24/outline';
import { Dialog, Transition } from '@headlessui/react';
import Footer from '../components/Footer';
import PrimaryButton from '../components/PrimaryButton'; // Assicurati che il percorso sia corretto

const LoadingSpinner = ({ className = "h-6 w-6" }) => (
    <svg className={`animate-spin ${className}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
);

const CollectionCover = ({ col, API_URL }) => {
    const [coverImageId, setCoverImageId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (col.cover_image_id || col.preview_image_id) {
            setCoverImageId(col.cover_image_id || col.preview_image_id);
            return;
        }

        const fetchTargetImage = async () => {
            setLoading(true);
            try {
                const response = await fetch(`${API_URL}/collections/${col.id}/images?page=1&limit=50`);

                if (response.ok && response.status !== 204) {
                    const data = await response.json();
                    const imageList = data.data || [];

                    const targetImage = imageList.find(img => String(img.title).trim() === "1");

                    if (targetImage) {
                        setCoverImageId(targetImage.id);
                    } else if (imageList.length > 0) {
                        setCoverImageId(imageList[0].id);
                    }
                }
            } catch (error) {
                console.error("Errore nel recupero dell'immagine di copertina:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchTargetImage();
    }, [col, API_URL]);

    if (loading) {
        return <div className="w-full h-full bg-earth-200 dark:bg-earth-800 animate-pulse" />;
    }

    if (coverImageId) {
        return (
            <img
                src={`${API_URL}/images/${coverImageId}/lq`}
                alt={col.name}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                loading="lazy"
            />
        );
    }

    return (
        <div className="w-full h-full flex flex-col items-center justify-center text-earth-400 bg-earth-200/50 dark:bg-earth-800/50">
            <FolderIcon className="h-14 w-14 stroke-1 mb-2" />
            <span className="text-xs font-medium text-earth-500">Empty Collection</span>
        </div>
    );
};


const Wallpapers = () => {
    const [collections, setCollections] = useState([]);
    const [activeTab, setActiveTab] = useState('all');
    const [selectedCollection, setSelectedCollection] = useState(null);
    const [images, setImages] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, total_pages: 1 });

    const [selectedImage, setSelectedImage] = useState(null);

    // NUOVO: Stato per controllare la visibilità del pulsante "Back to Top"
    const [showScrollTop, setShowScrollTop] = useState(false);

    // Stati e Riferimenti dedicati al bilanciamento geometrico basato sulle proporzioni reali
    const [masonryColumns, setMasonryColumns] = useState([[], [], []]);
    const [isCalculatingMasonry, setIsCalculatingMasonry] = useState(false);

    const API_URL = process.env.NODE_ENV === 'production' ? '/api' : (process.env.REACT_APP_API_URL || 'http://localhost:8080');

    // MODIFICATO: Inserito l'ascoltatore dello scroll per mostrare/nascondere il pulsante "Back to Top"
    useEffect(() => {
        window.scrollTo(0, 0);

        const handleScrollVisibility = () => {
            if (window.scrollY > 300) {
                setShowScrollTop(true);
            } else {
                setShowScrollTop(false);
            }
        };

        window.addEventListener('scroll', handleScrollVisibility);
        return () => window.removeEventListener('scroll', handleScrollVisibility);
    }, []);

    useEffect(() => {
        if (isLoading) return;

        if (!images || images.length === 0) {
            setMasonryColumns([[], [], []]);
            return;
        }

        const calculatePerfectMasonry = async () => {
            setIsCalculatingMasonry(true);
            const numColumns = 3;
            const computedColumns = Array.from({ length: numColumns }, () => []);
            const computedHeights = Array(numColumns).fill(0);

            const getImageDimensions = (imgObj) => {
                return new Promise((resolve) => {
                    const htmlImg = new Image();
                    htmlImg.src = `${API_URL}/images/${imgObj.id}/lq`;

                    htmlImg.onload = () => {
                        const ratio = htmlImg.naturalHeight / htmlImg.naturalWidth;
                        resolve({ imgObj, ratio });
                    };

                    htmlImg.onerror = () => {
                        resolve({ imgObj, ratio: 1 });
                    };
                });
            };

            try {
                const results = await Promise.all(images.map(img => getImageDimensions(img)));

                results.forEach(({ imgObj, ratio }) => {
                    const shortestColIdx = computedHeights.indexOf(Math.min(...computedHeights));
                    computedColumns[shortestColIdx].push(imgObj);
                    computedHeights[shortestColIdx] += ratio + 0.1;
                });

                setMasonryColumns(computedColumns);
            } catch (err) {
                console.error("Errore nel calcolo geometrico del Masonry:", err);
            } finally {
                setIsCalculatingMasonry(false);
            }
        };

        calculatePerfectMasonry();
    }, [images, isLoading]);

    const fetchCollections = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collections`);
            if (response.status === 204) {
                setCollections([]);
                return;
            }
            if (response.ok) {
                const data = await response.json();
                setCollections(data || []);
            } else {
                throw new Error('Failed to fetch collections');
            }
        } catch (error) {
            console.error('Error fetching collections:', error);
            setCollections([]);
        }
    }, [API_URL]);

    const fetchImages = useCallback(async (collectionId = null, page = 1, query = '', append = false) => {
        if (append) {
            setIsLoadingMore(true);
        } else {
            setIsLoading(true);
        }

        try {
            let url = '';

            if (query.trim()) {
                url = `${API_URL}/search/images?q=${encodeURIComponent(query.trim())}&page=${page}&limit=12`;
            } else if (collectionId) {
                url = `${API_URL}/collections/${collectionId}/images?page=${page}&limit=12`;
            } else {
                url = `${API_URL}/images/?page=${page}&limit=12`;
            }

            const response = await fetch(url);

            if (response.status === 204) {
                if (!append) setImages([]);
                setPagination({ page: 1, total_pages: 1 });
                return;
            }

            if (response.ok) {
                const data = await response.json();
                const processedImages = (data.data || []).map(item =>
                    typeof item === 'number' ? { id: item, title: `Photo #${item}`, description: '' } : item
                );

                setImages(prevImages => append ? [...prevImages, ...processedImages] : processedImages);
                setPagination({ page: data.page, total_pages: data.total_pages });
            } else {
                throw new Error('Failed to fetch images');
            }
        } catch (error) {
            console.error('Error fetching images:', error);
            if (!append) setImages([]);
            setPagination({ page: 1, total_pages: 1 });
        } finally {
            setIsLoading(false);
            setIsLoadingMore(false);
        }
    }, [API_URL]);

    useEffect(() => {
        fetchCollections();
    }, [fetchCollections]);

    useEffect(() => {
        if (activeTab === 'all' && !searchQuery.trim()) {
            fetchImages(selectedCollection, 1, '');
        }
    }, [activeTab, selectedCollection, searchQuery, fetchImages]);

    const handleSelectCollectionFilter = (id) => {
        setSelectedCollection(id);
        setActiveTab('all');
        setSearchQuery('');
        fetchImages(id, 1, '', false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleResetFilters = () => {
        setSelectedCollection(null);
        setActiveTab('all');
        setSearchQuery('');
        fetchImages(null, 1, '', false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setSelectedCollection(null);
        setActiveTab('all');
        fetchImages(null, 1, searchQuery, false);
    };

    const handleLoadMore = () => {
        const nextPage = pagination.page + 1;
        fetchImages(selectedCollection, nextPage, searchQuery, true);
    };

    const downloadHQ = (id) => {
        window.open(`${API_URL}/images/${id}/hq`, '_blank');
    };

    const getSelectedImageCollectionName = () => {
        if (!selectedImage || !selectedImage.collection_id) return 'Wallpaper';
        const found = collections.find(c => c.id === selectedImage.collection_id);
        return found ? found.name : 'Wallpaper';
    };

    // NUOVA FUNZIONE: Esegue l'azione di scroll fluido fino alla cima assoluta del viewport
    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const currentActiveCollection = collections.find(c => c.id === selectedCollection);

    return (
        <main className="min-h-screen bg-earth-50 dark:bg-earth-900 pt-28 px-4 lg:px-8 transition-colors duration-300 relative">
            <div className="max-w-7xl mx-auto mb-12">
                <section className="text-center mb-16">
                    <h1 className="text-5xl font-bold text-earth-900 dark:text-earth-50 mb-4">Wallpapers</h1>
                    <p className="text-earth-700 dark:text-earth-200 max-w-2xl mx-auto">
                        A curated collection of my photography. High-quality wallpapers for your desktop and mobile devices.
                    </p>
                </section>

                {/* Search and Main Tabs */}
                <div className="mb-12 space-y-8">
                    <form onSubmit={handleSearch} className="max-w-xl mx-auto relative">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search images (e.g. 'sunset over mountains')"
                            className="w-full pl-12 pr-4 py-4 bg-white dark:bg-earth-800 border border-earth-100 dark:border-earth-700 rounded-2xl shadow-sm text-earth-900 dark:text-earth-50 outline-none focus:ring-2 focus:ring-earth-400 transition-all"
                        />
                        <div className="absolute left-4 top-1/2 -translate-y-1/2">
                            {isLoading ? (
                                <LoadingSpinner className="h-6 w-6 text-earth-400" />
                            ) : (
                                <MagnifyingGlassIcon className="h-6 w-6 text-earth-400" />
                            )}
                        </div>
                    </form>

                    <div className="flex flex-wrap justify-center gap-3">
                        <button
                            onClick={handleResetFilters}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                                activeTab === 'all' && !selectedCollection
                                ? 'bg-earth-900 text-earth-50'
                                : 'bg-white dark:bg-earth-800 text-earth-700 dark:text-earth-200 hover:bg-earth-100 dark:hover:bg-earth-700'
                            }`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => { setActiveTab('collections'); }}
                            className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                                activeTab === 'collections'
                                ? 'bg-earth-900 text-earth-50'
                                : 'bg-white dark:bg-earth-800 text-earth-700 dark:text-earth-200 hover:bg-earth-100 dark:hover:bg-earth-700'
                            }`}
                        >
                            Collections
                        </button>
                    </div>

                    {selectedCollection && activeTab === 'all' && (
                        <div className="flex justify-center">
                            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-earth-200/60 dark:bg-earth-800 text-sm font-medium text-earth-800 dark:text-earth-100 border border-earth-300/30">
                                <span>Collection: <strong>{currentActiveCollection?.name}</strong></span>
                                <button onClick={handleResetFilters} className="p-0.5 rounded-full hover:bg-earth-300 dark:hover:bg-earth-700">
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            </span>
                        </div>
                    )}
                </div>

                {/* LOGICA DI RENDERING DELLE SEZIONI */}

                {activeTab === 'collections' ? (
                    <div className="space-y-8">
                        {collections.length > 0 ? (
                            collections.map((col, index) => (
                                <article
                                    key={col.id}
                                    className="w-full overflow-hidden rounded-2xl border border-earth-200 bg-earth-100/70 dark:border-earth-700 dark:bg-earth-800/80 transition-all hover:shadow-md"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 md:h-[320px] lg:h-[340px] md:items-stretch">
                                        <div className={`${index % 2 === 1 ? 'md:order-2' : 'md:order-1'} flex h-full flex-col justify-between gap-5 p-6 sm:p-8 lg:p-10`}>
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 text-earth-500">
                                                    <FolderIcon className="h-5 w-5" />
                                                    <span className="text-xs font-semibold tracking-wider uppercase">Collection</span>
                                                </div>
                                                <h3 className="text-2xl sm:text-3xl font-bold text-earth-900 dark:text-earth-50 leading-tight">
                                                    {col.name}
                                                </h3>
                                                <p className="text-base sm:text-lg text-earth-700 dark:text-earth-100 leading-relaxed line-clamp-3">
                                                    {col.description || "Explore this beautifully curated selection of photography."}
                                                </p>
                                            </div>
                                            <PrimaryButton
                                                className="w-fit"
                                                onClick={() => handleSelectCollectionFilter(col.id)}
                                            >
                                                View Collection
                                            </PrimaryButton>
                                        </div>

                                        <div className={`${index % 2 === 1 ? 'md:order-1' : 'md:order-2'} h-full min-h-[220px] sm:min-h-[260px] md:min-h-full bg-earth-200 dark:bg-earth-700 relative overflow-hidden`}>
                                            <CollectionCover col={col} API_URL={API_URL} />
                                        </div>
                                    </div>
                                </article>
                            ))
                        ) : (
                            <div className="text-center py-24 text-earth-500">
                                <p className="text-lg font-medium">No collections found.</p>
                            </div>
                        )}
                    </div>
                ) : (
                    /* RENDER DELLA GRIGLIA IMMAGINI */
                    <>
                        {selectedCollection && currentActiveCollection?.description && (
                            <div className="w-full mx-auto text-center mb-12 animate-fade-in">
                                <p className="text-lg text-earth-700 dark:text-earth-300 leading-relaxed italic border-l-4 sm:border-l-0 sm:border-y border-earth-200 dark:border-earth-700 py-4 px-6 sm:px-0">
                                    "{currentActiveCollection.description}"
                                </p>
                            </div>
                        )}

                        {(isLoading || isCalculatingMasonry) && masonryColumns[0].length === 0 ? (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {[...Array(3)].map((_, i) => (
                                    <div key={i} className="w-full aspect-[4/5] bg-earth-200 dark:bg-earth-800 rounded-2xl flex items-center justify-center">
                                        <LoadingSpinner className="h-10 w-10 text-earth-900 opacity-50" />
                                    </div>
                                ))}
                            </div>
                        ) : images.length > 0 ? (
                            <>
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
                                    {masonryColumns.map((colImages, colIdx) => (
                                        <div key={colIdx} className="flex flex-col gap-4 w-full">
                                            {colImages.map((img) => (
                                                <div
                                                    key={img.id}
                                                    onClick={() => setSelectedImage(img)}
                                                    className="w-full group relative overflow-hidden rounded-2xl bg-earth-200 dark:bg-earth-800 shadow-md cursor-zoom-in break-inside-avoid animate-fade-in"
                                                >
                                                    <img
                                                        src={`${API_URL}/images/${img.id}/lq`}
                                                        alt={img.title || "Wallpaper preview"}
                                                        className="w-full h-auto object-contain block select-none transition-transform duration-500 group-hover:scale-105"
                                                        loading="lazy"
                                                    />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col items-center justify-center p-4 text-center">
                                                        <span className="text-white font-bold text-lg translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                                                            View Details
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ))}
                                </div>

                                {isLoadingMore && (
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
                                        {[1, 2, 3].map(i => (
                                            <div key={i} className="w-full h-32 bg-earth-200 dark:bg-earth-800 animate-pulse rounded-2xl" />
                                        ))}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="text-center py-24 text-earth-500 flex flex-col items-center justify-center">
                                <MagnifyingGlassIcon className="h-12 w-12 text-earth-300 dark:text-earth-700 mb-4" />
                                <p className="text-lg font-medium text-earth-700 dark:text-earth-200">No images found.</p>
                                <p className="text-sm mt-2">Select a collection or try a different search term.</p>
                            </div>
                        )}

                        {/* Pulsante Load More */}
                        {!isLoading && pagination.page < pagination.total_pages && (
                            <div className="mt-16 flex justify-center">
                                <button
                                    disabled={isLoadingMore}
                                    onClick={handleLoadMore}
                                    className="flex items-center gap-2 px-8 py-3 bg-earth-900 text-earth-50 dark:bg-earth-100 dark:text-earth-900 font-semibold rounded-xl shadow-md transition-all hover:bg-earth-800 dark:hover:bg-earth-200 disabled:opacity-50 hover:scale-[1.02] active:scale-95"
                                >
                                    {isLoadingMore ? (
                                        <>
                                            <LoadingSpinner />
                                            <span>Loading...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span>Load More</span>
                                            <ArrowDownIcon className="h-5 w-5 animate-bounce mt-0.5" />
                                        </>
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            <Footer />

            <div className={`fixed bottom-8 right-8 z-40 transition-all duration-300 transform ${
                showScrollTop ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-75 pointer-events-none'
            }`}>
                <button
                    onClick={scrollToTop}
                    title="Back to top"
                    className="p-3.5 bg-earth-900 text-earth-50 dark:bg-earth-100 dark:text-earth-900 rounded-full shadow-xl hover:shadow-2xl transition-all group hover:scale-110 active:scale-95 border border-earth-100/10"
                >
                    <ArrowUpIcon className="h-6 w-6 stroke-[2.5] group-hover:-translate-y-0.5 transition-transform duration-200" />
                </button>
            </div>

            {/* Lightbox Modal */}
            <Transition show={!!selectedImage} as={Fragment}>
                <Dialog as="div" className="relative z-50" onClose={() => setSelectedImage(null)}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-auto max-w-5xl transform overflow-hidden rounded-3xl bg-white dark:bg-earth-800 p-0 text-left align-middle shadow-2xl transition-all border border-earth-100 dark:border-earth-700 mx-4 h-fit">
                                    <div className="flex flex-col lg:flex-row min-h-0 items-start">
                                        <div className="w-full lg:flex-1 bg-black flex items-center justify-center overflow-hidden">
                                            {selectedImage && (
                                                <img
                                                    src={`${API_URL}/images/${selectedImage.id}/lq`}
                                                    alt={selectedImage.title}
                                                    className="w-full h-auto max-h-[75vh] object-contain block select-none"
                                                    loading="lazy"
                                                />
                                            )}
                                        </div>

                                        <div className="w-full lg:w-[360px] flex-shrink-0 px-6 py-8 flex flex-col justify-between bg-white dark:bg-earth-800 border-t lg:border-t-0 lg:border-l border-earth-100 dark:border-earth-700 self-stretch">
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-start">
                                                    <Dialog.Title as="h2" className="text-3xl font-bold text-earth-900 dark:text-earth-50 truncate pr-4 leading-tight">
                                                        {getSelectedImageCollectionName()}
                                                    </Dialog.Title>
                                                    <button
                                                        onClick={() => setSelectedImage(null)}
                                                        className="p-2 -mt-2 -mr-2 text-earth-400 hover:text-earth-900 dark:hover:text-earth-50 transition-colors flex-shrink-0"
                                                    >
                                                        <XMarkIcon className="h-6 w-6" />
                                                    </button>
                                                </div>
                                                <div className="pt-6 border-t border-earth-100 dark:border-earth-700">
                                                    <h4 className="text-sm font-semibold text-earth-400 uppercase tracking-wider mb-2">Details</h4>
                                                    <div className="grid grid-cols-2 gap-4 text-sm">
                                                        <div>
                                                            <p className="text-earth-500">Format</p>
                                                            <p className="text-earth-900 dark:text-earth-50 font-medium">JPEG</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="mt-8 pt-4">
                                                <button
                                                    onClick={() => selectedImage && downloadHQ(selectedImage.id)}
                                                    className="w-full flex items-center justify-center gap-3 bg-earth-900 text-earth-50 dark:bg-earth-100 dark:text-earth-900 py-4 px-6 rounded-2xl font-bold text-lg hover:bg-earth-800 dark:hover:bg-earth-200 transition-all hover:scale-[1.02] active:scale-95"
                                                >
                                                    <ArrowDownTrayIcon className="h-6 w-6" />
                                                    Download
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </main>
    );
};

export default Wallpapers;
